import React, { useState, useEffect, useCallback } from 'react';
import { sales } from '../services/api';
import ProspectingSetup from './ProspectingSetup';
import EmailIntegration from './EmailIntegration';

// Stage config — display label, color, default probability
const STAGES = [
  { id: 'appointment_scheduled', label: 'Appt Set', color: '#3b82f6', prob: 0.20 },
  { id: 'discovery',              label: 'Discovery', color: '#8b5cf6', prob: 0.40 },
  { id: 'proposal_sent',          label: 'Proposal', color: '#f59e0b', prob: 0.60 },
  { id: 'negotiation',            label: 'Negotiation', color: '#ef4444', prob: 0.80 },
  { id: 'closed_won',             label: 'Won', color: '#10b981', prob: 1.00 },
  { id: 'closed_lost',            label: 'Lost', color: '#6b7280', prob: 0.00 },
];

const formatCurrency = (n) => {
  if (n == null || isNaN(n)) return '$0';
  if (n >= 1000) return `$${(n/1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

const formatDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const sentimentColor = (s) => {
  if (s === 'positive') return '#10b981';
  if (s === 'negative') return '#ef4444';
  if (s === 'neutral')  return '#6b7280';
  return '#9ca3af';
};

const intentLabel = (i) => {
  const map = {
    qualified_handoff: '🤝 Handoff',
    bounce_recovery: '🔁 Bounce',
    interested: '✨ Interested',
    not_interested: '❌ Not interested',
    out_of_office: '🏖️ OOO',
    question: '❓ Question',
  };
  return map[i] || i;
};

function DealCard({ deal, onClick, onStageChange }) {
  const stop = (e) => e.stopPropagation();
  return (
    <div
      onClick={() => onClick(deal)}
      style={{
        background: '#1f2937',
        border: '1px solid #374151',
        borderRadius: 6,
        padding: 10,
        marginBottom: 8,
        cursor: 'pointer',
        fontSize: 13,
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = '#60a5fa'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#374151'}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#f3f4f6' }}>
        {deal.company || deal.contact_name || 'Untitled'}
      </div>
      {deal.contact_name && deal.company !== deal.contact_name && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{deal.contact_name}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 10, color: '#6b7280' }}>{formatDate(deal.updated_at)}</span>
        {deal.amount > 0 && (
          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>{formatCurrency(deal.amount)}</span>
        )}
      </div>
    </div>
  );
}

function StageColumn({ stage, deals, onDealClick }) {
  const stageDeals = deals.filter(d => d.pipeline_stage === stage.id);
  const totalValue = stageDeals.reduce((sum, d) => sum + (d.amount || 0), 0);
  return (
    <div style={{
      flex: '0 0 220px',
      background: '#111827',
      borderRadius: 8,
      padding: 12,
      marginRight: 12,
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: `2px solid ${stage.color}`,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#f3f4f6' }}>{stage.label}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{stageDeals.length} {stageDeals.length === 1 ? 'deal' : 'deals'}</div>
        </div>
        {totalValue > 0 && (
          <div style={{ fontSize: 11, color: stage.color, fontWeight: 600 }}>
            {formatCurrency(totalValue)}
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {stageDeals.length === 0 ? (
          <div style={{ color: '#4b5563', fontSize: 11, textAlign: 'center', padding: 20 }}>
            No deals
          </div>
        ) : (
          stageDeals.map(d => <DealCard key={d.id} deal={d} onClick={onDealClick} />)
        )}
      </div>
    </div>
  );
}

function DealDetailModal({ deal, onClose, onStageChange, onDelete }) {
  const [newStage, setNewStage] = useState(deal.pipeline_stage);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    if (newStage === deal.pipeline_stage) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await sales.updateDeal(deal.id, { pipeline_stage: newStage });
      onStageChange(deal.id, newStage);
      onClose();
    } catch (e) {
      alert('Failed to update: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete deal for ${deal.company || deal.contact_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await sales.deleteDeal(deal.id);
      onDelete(deal.id);
      onClose();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>📊 {deal.company || deal.contact_name || 'Deal'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Contact</div>
              <div style={{ fontWeight: 500 }}>{deal.contact_name || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Email</div>
              <div style={{ fontSize: 12 }}>
                {deal.contact_email ? (
                  <a href={`mailto:${deal.contact_email}`} style={{ color: '#60a5fa' }}>{deal.contact_email}</a>
                ) : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Industry</div>
              <div>{deal.industry || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Amount</div>
              <div style={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(deal.amount)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Probability</div>
              <div>{Math.round((deal.probability || 0) * 100)}%</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>Source</div>
              <div style={{ fontSize: 12 }}>{deal.source || '—'}</div>
            </div>
          </div>

          {deal.notes && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#d1d5db', background: '#0f172a', padding: 8, borderRadius: 4 }}>
                {deal.notes}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Move to stage</div>
            <select
              value={newStage}
              onChange={e => setNewStage(e.target.value)}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            >
              {STAGES.map(s => (
                <option key={s.id} value={s.id}>{s.label} ({Math.round(s.prob * 100)}%)</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || newStage === deal.pipeline_stage}
              style={{ flex: 1 }}
            >
              {saving ? 'Saving…' : newStage === deal.pipeline_stage ? 'No change' : 'Save Stage'}
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : '🗑️ Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewDealModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: '',
    company: '',
    contact_name: '',
    contact_email: '',
    industry: '',
    amount: 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_email) {
      alert('Contact email is required');
      return;
    }
    setSaving(true);
    try {
      const created = await sales.createDeal(form);
      onCreate(created.data || created);
      onClose();
    } catch (err) {
      alert('Failed to create: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>➕ New Deal</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Company *</label>
            <input
              required
              type="text"
              value={form.company}
              onChange={e => setForm({ ...form, company: e.target.value, name: e.target.value })}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Contact name</label>
            <input
              type="text"
              value={form.contact_name}
              onChange={e => setForm({ ...form, contact_name: e.target.value })}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Contact email *</label>
            <input
              required
              type="email"
              value={form.contact_email}
              onChange={e => setForm({ ...form, contact_email: e.target.value })}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Industry</label>
            <input
              type="text"
              value={form.industry}
              onChange={e => setForm({ ...form, industry: e.target.value })}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Amount ($)</label>
            <input
              type="number"
              min="0"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={3}
              style={{ width: '100%', padding: 8, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Creating…' : 'Create Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// === CRM FEATURES (2026-06-10) ===
// Tabs: Deals | Contacts | Tasks | Sends. Do-not-email toggle inline in Contacts.

// --- Contacts view ---
function ContactsView({ onToast }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [needsDraftOnly, setNeedsDraftOnly] = useState(false);
  const [busy, setBusy] = useState({});
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await sales.listContacts({ limit: 500 });
      setContacts(r.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = contacts.filter(c => {
    if (showBlockedOnly && !c.do_not_email) return false;
    if (needsDraftOnly && ((c.email_sent_count || 0) > 0 || c.do_not_email)) return false;
    if (!q) return true;
    const lq = q.toLowerCase();
    return (c.name || '').toLowerCase().includes(lq) ||
           (c.email || '').toLowerCase().includes(lq) ||
           (c.company || '').toLowerCase().includes(lq) ||
           (c.industry || '').toLowerCase().includes(lq);
  });

  // CRM-style "email history" badge for each contact
  const emailBadge = (c) => {
    const n = c.email_sent_count || 0;
    if (c.do_not_email) return { txt: '🚫 blocked', bg: '#7f1d1d', fg: '#fecaca' };
    if (n === 0)        return { txt: '📭 never',   bg: '#1e293b', fg: '#94a3b8' };
    const last = c.last_email_status || 'sent';
    const bg = last === 'sent' ? '#065f46' : last === 'failed' ? '#7f1d1d' : '#1e3a8a';
    const fg = last === 'sent' ? '#a7f3d0' : last === 'failed' ? '#fecaca' : '#bfdbfe';
    return { txt: `📨 ${n}× (${last})`, bg, fg, when: c.last_emailed_at };
  };

  const toggleFlag = async (c) => {
    setBusy(b => ({ ...b, [c.id]: true }));
    try {
      const newVal = !c.do_not_email;
      const reason = newVal
        ? // eslint-disable-next-line no-restricted-globals
          (prompt('Reason for blocking? (e.g. "asked to be removed 2026-06-10")', c.do_not_email_reason || '') || 'no reason given')
        : null;
      const r = await sales.flagContact(c.id, newVal, reason);
      setContacts(cs => cs.map(x => x.id === c.id ? r.data : x));
      onToast && onToast(newVal ? `🚫 Blocked: ${c.email}` : `✅ Unblocked: ${c.email}`);
    } catch (e) {
      onToast && onToast(`❌ Flag failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(b => ({ ...b, [c.id]: false }));
    }
  };

  const runBackfill = async () => {
    setBusy(b => ({ ...b, _backfill: true }));
    try {
      const r = await sales.backfillContacts();
      onToast && onToast(`✅ Backfill: ${r.data.created} created, ${r.data.updated} updated, ${r.data.skipped} skipped (of ${r.data.total_deals} deals)`);
      load();
    } catch (e) {
      onToast && onToast(`❌ Backfill failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(b => ({ ...b, _backfill: false }));
    }
  };

  const runHistoryBackfill = async () => {
    setBusy(b => ({ ...b, _historyBackfill: true }));
    try {
      const r = await sales.backfillSendHistory();
      onToast && onToast(`📨 Email history: ${r.data.stamped} stamped · ${r.data.contacted} contacted, ${r.data.needs_draft} still need a first email`);
      load();
    } catch (e) {
      onToast && onToast(`❌ History backfill failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(b => ({ ...b, _historyBackfill: false }));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text" placeholder="Search contacts…"
          value={q} onChange={e => setQ(e.target.value)}
          style={{ flex: '0 0 260px', padding: '6px 10px', background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>
          <input type="checkbox" checked={showBlockedOnly} onChange={e => setShowBlockedOnly(e.target.checked)} />
          Show blocked only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: needsDraftOnly ? '#f59e0b' : '#9ca3af', cursor: 'pointer', fontWeight: needsDraftOnly ? 600 : 400 }}>
          <input type="checkbox" checked={needsDraftOnly} onChange={e => setNeedsDraftOnly(e.target.checked)} />
          📭 Needs first draft
        </label>
        <button className="btn btn-secondary btn-sm" onClick={runBackfill} disabled={busy._backfill}>
          {busy._backfill ? 'Working…' : '🔄 Backfill from deals'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={runHistoryBackfill} disabled={busy._historyBackfill} title="Compute email_sent_count / last_emailed_at from historical sales_sends">
          {busy._historyBackfill ? 'Working…' : '📨 Backfill history'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>↻ Refresh</button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>➕ New Contact</button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          {filtered.length} of {contacts.length} contacts · {contacts.filter(c => c.do_not_email).length} blocked ·{' '}
          <span style={{ color: '#f59e0b', fontWeight: 600 }}>{contacts.filter(c => !c.do_not_email && (c.email_sent_count || 0) === 0).length} need draft</span> ·{' '}
          <span style={{ color: '#a7f3d0' }}>{contacts.filter(c => (c.email_sent_count || 0) > 0).length} contacted</span>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}

      {loading && contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading contacts…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          {contacts.length === 0
            ? 'No contacts yet. Click "Backfill from deals" to import your HubSpot list.'
            : 'No contacts match the current filter.'}
        </div>
      ) : (
        <div style={{ background: '#111827', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#9ca3af', textTransform: 'uppercase', fontSize: 10 }}>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Company</th>
                <th style={th}>Industry</th>
                <th style={th}>Deals</th>
                <th style={th}>📨 Email</th>
                <th style={th}>Last update</th>
                <th style={th}>🚫 Email</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const badge = emailBadge(c);
                return (
                <tr key={c.id} style={{ borderTop: '1px solid #1f2937', color: '#e5e7eb' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{c.name || '—'}</div>
                    {c.title && <div style={{ fontSize: 10, color: '#6b7280' }}>{c.title}</div>}
                  </td>
                  <td style={td}>
                    <a href={`mailto:${c.email}`} style={{ color: '#60a5fa' }}>{c.email}</a>
                  </td>
                  <td style={td}>{c.company || '—'}</td>
                  <td style={td}>{c.industry || '—'}</td>
                  <td style={td}>
                    <span style={{ padding: '2px 6px', background: c.deal_count > 0 ? '#1e3a8a' : '#1f2937', borderRadius: 8, fontSize: 10 }}>
                      {c.deal_count || 0}
                    </span>
                  </td>
                  <td style={td}>
                    <span title={badge.when ? `Last: ${formatDate(badge.when)}` : 'No email history'}
                      style={{ padding: '2px 6px', background: badge.bg, color: badge.fg, borderRadius: 8, fontSize: 10, whiteSpace: 'nowrap' }}>
                      {badge.txt}
                    </span>
                    {badge.when && <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>{formatDate(badge.when)}</div>}
                  </td>
                  <td style={td}>{formatDate(c.last_deal_update || c.updated_at)}</td>
                  <td style={td}>
                    {c.do_not_email ? (
                      <button
                        onClick={() => toggleFlag(c)}
                        disabled={busy[c.id]}
                        title={`Blocked by ${c.do_not_email_by || '?'} on ${formatDate(c.do_not_email_at)}: ${c.do_not_email_reason || 'no reason'}`}
                        style={{ background: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}
                      >
                        🚫 BLOCKED {busy[c.id] ? '…' : ''}
                      </button>
                    ) : (
                      <button
                        onClick={() => toggleFlag(c)}
                        disabled={busy[c.id]}
                        style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: 4, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}
                      >
                        ✓ Allowed {busy[c.id] ? '…' : ''}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <NewContactModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 600 };
const td = { padding: '8px 10px', verticalAlign: 'top' };

function NewContactModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', industry: '', title: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { alert('Name and email required'); return; }
    setSaving(true);
    try {
      await sales.createContact(form);
      onCreated();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status === 409) {
        alert(`Contact already exists (id ${err.response.data.id})`);
        onCreated();
      } else {
        alert('Failed: ' + msg);
      }
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>➕ New Contact</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          {['name', 'email', 'company', 'industry', 'title', 'phone'].map(k => (
            <div key={k} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>{k}{k === 'name' || k === 'email' ? ' *' : ''}</label>
              <input
                required={k === 'name' || k === 'email'}
                type={k === 'email' ? 'email' : 'text'}
                value={form[k]}
                onChange={e => setForm({ ...form, [k]: e.target.value })}
                style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Creating…' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Tasks view ---
function TasksView({ onToast }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await sales.listTasks({ limit: 200 });
      setTasks(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = tasks.filter(t => showCompleted || t.status !== 'completed');
  const overdue = filtered.filter(t => t.status === 'pending' && t.due_date && new Date(t.due_date) < new Date()).length;

  const complete = async (t) => {
    try {
      await sales.updateTask(t.id, { status: 'completed' });
      load();
    } catch (e) { onToast && onToast(`❌ ${e.response?.data?.error || e.message}`); }
  };
  const remove = async (t) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete task "${t.title}"?`)) return;
    try { await sales.deleteTask(t.id); load(); }
    catch (e) { onToast && onToast(`❌ ${e.response?.data?.error || e.message}`); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Show completed
        </label>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>↻ Refresh</button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>➕ New Task</button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: overdue > 0 ? '#ef4444' : '#9ca3af', fontWeight: overdue > 0 ? 600 : 400 }}>
          {overdue > 0 ? `⚠️ ${overdue} overdue · ` : ''}{filtered.length} task{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          No tasks{showCompleted ? '' : ' pending'}. Click "New Task" to add a follow-up.
        </div>
      ) : (
        <div style={{ background: '#111827', borderRadius: 6, overflow: 'hidden' }}>
          {filtered.map(t => {
            const isOverdue = t.status === 'pending' && t.due_date && new Date(t.due_date) < new Date();
            return (
              <div key={t.id} style={{
                padding: 10, borderTop: '1px solid #1f2937',
                display: 'flex', alignItems: 'center', gap: 10,
                background: isOverdue ? '#1c0a0a' : 'transparent',
              }}>
                <input type="checkbox" checked={t.status === 'completed'} onChange={() => complete(t)} style={{ cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: t.status === 'completed' ? '#6b7280' : '#f3f4f6', textDecoration: t.status === 'completed' ? 'line-through' : 'none', fontWeight: 500 }}>
                    {t.title}
                  </div>
                  {t.description && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{t.description}</div>}
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3, display: 'flex', gap: 8 }}>
                    {t.due_date && <span style={{ color: isOverdue ? '#ef4444' : '#9ca3af', fontWeight: isOverdue ? 600 : 400 }}>📅 {formatDate(t.due_date)} {isOverdue && '(overdue)'}</span>}
                    {t.priority && t.priority !== 'medium' && <span>priority: {t.priority}</span>}
                    {t.deal_contact_name && <span>→ {t.deal_contact_name} ({t.deal_company || '?'})</span>}
                    {t.assignee && <span>@{t.assignee}</span>}
                  </div>
                </div>
                <button onClick={() => remove(t)} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <NewTaskModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function NewTaskModal({ onClose, onCreated }) {
  const [deals, setDeals] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', deal_id: '', priority: 'medium', due_date: '', type: 'follow_up' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { sales.listDeals({ limit: 100 }).then(r => setDeals(r.data || [])).catch(() => {}); }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { alert('Title required'); return; }
    setSaving(true);
    try {
      await sales.createTask({
        ...form,
        deal_id: form.deal_id ? parseInt(form.deal_id) : null,
        due_date: form.due_date || null,
      });
      onCreated();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>➕ New Task</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Title *</label>
            <input required type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Due date</label>
              <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }}>
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3 }}>Linked deal</label>
            <select value={form.deal_id} onChange={e => setForm({ ...form, deal_id: e.target.value })}
              style={{ width: '100%', padding: 6, background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }}>
              <option value="">— none —</option>
              {deals.map(d => <option key={d.id} value={d.id}>{d.company || d.contact_name} ({d.contact_email})</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Creating…' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Sends view (audit log + draft/approve/queue workflow) ---
// 4 sub-tabs: Drafts | Approved | Sent | All — plus Rejected in "All".
// Drafts show Approve / Send-Now / Reject buttons; Approved show Send-Now / Revert-to-Draft.
// Each row expands to show the email body preview.
function DraftEditor({ send, onClose, onSaved, onToast }) {
  const [subject, setSubject] = useState(send.subject || '');
  const [body, setBody] = useState(send.body || '');
  const [saving, setSaving] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/sales/sends/${send.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body }),
      });
      const data = await res.json();
      if (data.error) { onToast && onToast(`Error: ${data.error}`); return; }
      onToast && onToast('✅ Draft saved');
      onSaved && onSaved();
    } catch (e) { onToast && onToast(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    setSaving(true);
    try {
      await fetch(`/api/sales/sends/${send.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, status: 'approved' }),
      });
      onToast && onToast('✅ Approved & queued');
      onSaved && onSaved();
      onClose();
    } catch (e) { onToast && onToast(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!rejectReason) { onToast && onToast('Please select a reason'); return; }
    setSaving(true);
    try {
      await fetch('/api/prospecting/feedback', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          send_id: send.id,
          reason: rejectReason,
          notes: rejectNotes,
          company_name: send.company_name || null,
          industry: send.industry || null,
        }),
      });
      await fetch(`/api/sales/send/${send.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      onToast && onToast('🗑️ Draft rejected — feedback recorded');
      onSaved && onSaved();
      onClose();
    } catch (e) { onToast && onToast(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="w-1/2 min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">To: {send.to_email}</div>
          {send.company_name && <div className="text-xs text-gray-400">Company: {send.company_name}</div>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl">✕</button>
      </div>
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Subject</label>
        <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm" />
      </div>
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Body</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm font-mono whitespace-pre-wrap" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={saveDraft} disabled={saving}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 disabled:opacity-50">
          {saving ? 'Saving...' : '💾 Save Draft'}
        </button>
        <button onClick={approve} disabled={saving}
          className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:opacity-50">
          {saving ? 'Working...' : '✓ Approve & Queue'}
        </button>
        <button onClick={() => setShowReject(true)} disabled={saving}
          className="px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-lg text-sm hover:bg-red-200 disabled:opacity-50">
          ✗ Reject
        </button>
      </div>
      {showReject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReject(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Why are you rejecting this draft?</h3>
            <div className="space-y-2 mb-4">
              {[
                ['wrong_industry', 'Wrong industry'],
                ['too_big', 'Company too big/small'],
                ['existing_customer', 'Already a customer'],
                ['bad_email', 'Bad email address'],
                ['wrong_voice', 'Email content doesn\'t fit our voice'],
                ['other', 'Other'],
              ].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="radio" name="rejectReason" value={val} checked={rejectReason === val}
                    onChange={e => setRejectReason(e.target.value)} />
                  {label}
                </label>
              ))}
            </div>
            <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={2}
              placeholder="Additional notes (optional)"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReject(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm">Cancel</button>
              <button onClick={reject} disabled={saving || !rejectReason}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50">
                {saving ? 'Working...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SendsView({ onToast }) {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('draft');   // draft | approved | sent | all
  const [expanded, setExpanded] = useState(null);  // send id currently showing body
  const [busy, setBusy] = useState(null);          // send id currently being PATCHed
  const [confirmSendId, setConfirmSendId] = useState(null);  // send id awaiting 2nd-click confirmation
  const [selectedDraft, setSelectedDraft] = useState(null);   // send object for split-view editor

  const reload = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (subTab === 'draft' || subTab === 'approved' || subTab === 'sent') params.status = subTab;
    sales.listSends(params).then(r => setSends(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [subTab]);

  const generateColdDrafts = async () => {
    /* eslint-disable-next-line no-restricted-globals */
    if (!confirm('Create a draft for every contact that has never been emailed? (skips blocked, Hunter-invalid, and existing drafts)')) return;
    setBusy('_cold');
    try {
      const r = await sales.draftsForColdContacts({ limit: 200 });
      onToast && onToast(`📝 Created ${r.data.created} drafts (${r.data.skipped} skipped, ${r.data.total_cold} cold contacts found)`);
      reload();
    } catch (e) {
      onToast && onToast(`❌ Cold-draft failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  // Auto-cancel the confirm state after 5s so a stale "are you sure" never lingers
  useEffect(() => {
    if (confirmSendId == null) return;
    const t = setTimeout(() => setConfirmSendId(null), 5000);
    return () => clearTimeout(t);
  }, [confirmSendId]);

  const patchSend = async (id, body) => {
    setBusy(id);
    try {
      await sales.patchSend(id, body);
      onToast && onToast(`Send #${id} → ${body.status}`);
      reload();
    } catch (e) {
      onToast && onToast(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  // Synchronous dispatch: PATCH status to 'queued' AND fire Graph immediately
  // (bypasses the every-15-min cron window — returns sent/failed in the response)
  //
  // 2-click confirmation pattern (no window.confirm() — those are easy to miss
  // or block silently in some browser configs, and they look un-native in a
  // SPA): first click arms the row, second click within 5s actually fires.
  const sendNow = async (s) => {
    if (confirmSendId !== s.id) {
      setConfirmSendId(s.id);
      onToast && onToast(`⚠️ Click Send Now again within 5s to confirm sending to ${s.to_email}`);
      return;
    }
    setConfirmSendId(null);
    setBusy(s.id);
    try {
      const r = await sales.dispatchNow(s.id);
      if (r.data?.status === 'sent') {
        onToast && onToast(`✅ Sent to ${s.to_email}`);
      } else if (r.data?.status === 'failed') {
        onToast && onToast(`❌ Failed: ${r.data.error || 'unknown'}`);
      } else {
        onToast && onToast(`Error: ${r.data?.error || 'unknown'}`);
      }
      reload();
    } catch (e) {
      onToast && onToast(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const statusColor = (s) => ({
    draft:    { bg: '#374151', fg: '#d1d5db' },
    approved: { bg: '#1e3a8a', fg: '#bfdbfe' },
    queued:   { bg: '#581c87', fg: '#e9d5ff' },
    sent:     { bg: '#065f46', fg: '#a7f3d0' },
    failed:   { bg: '#7f1d1d', fg: '#fecaca' },
    bounced:  { bg: '#7f1d1d', fg: '#fecaca' },
    complained: { bg: '#7f1d1d', fg: '#fecaca' },
  }[s] || { bg: '#374151', fg: '#d1d5db' });

  return (
    <div>
      <div className="flex gap-6">
      <div className={`${selectedDraft ? 'w-1/2 min-w-0' : 'w-full'}`}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[
          ['draft',    `📝 Drafts`],
          ['approved', `✅ Approved`],
          ['sent',     `📤 Sent`],
          ['all',      `📋 All`],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setSubTab(k)}
            className={'btn btn-sm ' + (subTab === k ? 'btn-primary' : 'btn-secondary')}
            style={{ fontSize: 12 }}>{label}</button>
        ))}
        <button onClick={generateColdDrafts} disabled={busy === '_cold'}
          className="btn btn-sm"
          style={{ fontSize: 12, marginLeft: 8, background: '#0e7490', color: 'white', border: 'none' }}
          title="Create a draft for every contact that has never been emailed">
          {busy === '_cold' ? '⏳ Working…' : '📝 Drafts for cold contacts'}
        </button>
        <button onClick={reload} className="btn btn-sm btn-secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>↻ Refresh</button>
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading…</div>
      : sends.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          {subTab === 'draft' && 'No drafts pending. The daily cron at 8 AM UTC will post drafts here.'}
          {subTab === 'approved' && 'No approved sends waiting for dispatch (every 15 min, 9 AM-5 PM UTC).'}
          {subTab === 'sent' && 'No sent emails yet.'}
          {subTab === 'all' && 'No sends logged yet.'}
        </div>
      ) : (
        <div style={{ background: '#111827', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#9ca3af', textTransform: 'uppercase', fontSize: 10 }}>
                <th style={th}>When</th>
                <th style={th}>To</th>
                <th style={th}>Subject</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sends.map(s => {
                const c = statusColor(s.status);
                const isBusy = busy === s.id;
                const isExpanded = expanded === s.id;
                return (
                  <React.Fragment key={s.id}>
                    <tr style={{ borderTop: '1px solid #1f2937', color: '#e5e7eb' }}>
                      <td style={td}>
                        <div>{formatDate(s.drafted_at || s.sent_at)}</div>
                        {s.approved_at && <div style={{ fontSize: 10, color: '#6b7280' }}>approved {formatDate(s.approved_at)} by {s.approved_by}</div>}
                        {s.queued_at && !s.sent_at && <div style={{ fontSize: 10, color: '#6b7280' }}>queued {formatDate(s.queued_at)}</div>}
                      </td>
                      <td style={td}>
                        <div>{s.to_email}</div>
                        {s.error && <div style={{ fontSize: 10, color: '#fca5a5' }} title={s.error}>{s.error.slice(0, 50)}{s.error.length > 50 ? '…' : ''}</div>}
                      </td>
                      <td style={td}>
                        <div className="cursor-pointer" onClick={() => s.status === 'draft' && setSelectedDraft(s)}>
                          {s.subject || '—'}
                        </div>
                        <button onClick={() => setExpanded(isExpanded ? null : s.id)}
                          style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 10, cursor: 'pointer', padding: 0, marginTop: 2 }}>
                          {isExpanded ? '▼ hide body' : '▶ preview body'}
                        </button>
                      </td>
                      <td style={td}>
                        <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: c.bg, color: c.fg }}>{s.status}</span>
                      </td>
                      <td style={td}>
                        {s.status === 'draft' && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button onClick={() => patchSend(s.id, { status: 'approved', approved_by: 'matt' })}
                              disabled={isBusy} className="btn btn-sm btn-primary" style={{ fontSize: 10, padding: '2px 8px' }}>
                              ✓ Approve
                            </button>
                            <button onClick={() => sendNow(s)}
                              disabled={isBusy}
                              className="btn btn-sm"
                              title={confirmSendId === s.id ? 'Click again to send' : 'Send this email now'}
                              style={{ fontSize: 10, padding: '2px 8px', background: confirmSendId === s.id ? '#dc2626' : '#7c3aed', color: 'white', border: 'none' }}>
                              {isBusy ? '⏳ Sending…' : confirmSendId === s.id ? '⚠️ Click to send' : '⚡ Send Now'}
                            </button>
                            <button onClick={() => {
                              // eslint-disable-next-line no-restricted-globals
                              if (confirm(`Delete draft to ${s.to_email}?`)) sales.deleteSend(s.id).then(reload).catch(e => onToast && onToast('Error: ' + e.message));
                            }}
                              disabled={isBusy} className="btn btn-sm" style={{ fontSize: 10, padding: '2px 8px', background: '#7f1d1d', color: 'white', border: 'none' }}>
                              ✗ Reject
                            </button>
                          </div>
                        )}
                        {s.status === 'approved' && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button onClick={() => sendNow(s)}
                              disabled={isBusy}
                              className="btn btn-sm"
                              title={confirmSendId === s.id ? 'Click again to send' : 'Send this email now'}
                              style={{ fontSize: 10, padding: '2px 8px', background: confirmSendId === s.id ? '#dc2626' : '#7c3aed', color: 'white', border: 'none' }}>
                              {isBusy ? '⏳ Sending…' : confirmSendId === s.id ? '⚠️ Click to send' : '⚡ Send Now'}
                            </button>
                            <button onClick={() => patchSend(s.id, { status: 'draft' })}
                              disabled={isBusy} className="btn btn-sm btn-secondary" style={{ fontSize: 10, padding: '2px 8px' }}>
                              ↶ Revert
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: '#0a0f1a' }}>
                        <td colSpan={5} style={{ padding: 16, color: '#cbd5e1', fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {s.body || '(no body)'}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
      {selectedDraft && (
        <DraftEditor
          send={selectedDraft}
          onClose={() => setSelectedDraft(null)}
          onSaved={() => { reload(); setSelectedDraft(null); }}
          onToast={onToast}
        />
      )}
      </div>
    </div>
  );
}

export default function SalesPipeline() {
  const [tab, setTab] = useState('deals');
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast({ msg, at: Date.now() }); setTimeout(() => setToast(t => t && Date.now() - t.at < 100 ? null : t), 3500); };
  return (
    <div style={{ padding: '20px 24px', maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>📊 Sales Pipeline</h1>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['deals','💼 Deals'],['contacts','👥 Contacts'],['tasks','✅ Tasks'],['sends','📤 Sends'],['replies','📨 Replies'],['prospecting','🎯 Prospecting'],['email','📧 Email']].map(([k,label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={'btn btn-sm ' + (tab === k ? 'btn-primary' : 'btn-secondary')}
              style={{ fontSize: 12 }}>{label}</button>
          ))}
        </div>
      </div>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#1f2937', color: '#f3f4f6', padding: '10px 16px', borderRadius: 6, borderLeft: '4px solid #3b82f6', zIndex: 1000, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
          {toast.msg}
        </div>
      )}
      {tab === 'deals'     && <DealsView onToast={showToast} />}
      {tab === 'contacts'  && <ContactsView onToast={showToast} />}
      {tab === 'tasks'     && <TasksView onToast={showToast} />}
      {tab === 'sends'     && <SendsView onToast={showToast} />}
      {tab === 'replies'   && <RepliesView onToast={showToast} />}
      {tab === 'prospecting' && <ProspectingSetup />}
      {tab === 'email'    && <EmailIntegration />}
    </div>
  );
}

// === Original Deals view (renamed for tab wrapper) ===
function DealsView() {
  const [deals, setDeals] = useState([]);
  const [replies, setReplies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [filter, setFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dealsRes, repliesRes, summaryRes] = await Promise.all([
        sales.listDeals({ limit: 500 }),
        sales.listReplies({ limit: 50 }),
        sales.pipelineSummary(),
      ]);
      setDeals(dealsRes.data || []);
      setReplies(repliesRes.data || []);
      setSummary(summaryRes.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredDeals = filter
    ? deals.filter(d =>
        (d.company || '').toLowerCase().includes(filter.toLowerCase()) ||
        (d.contact_name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (d.contact_email || '').toLowerCase().includes(filter.toLowerCase()) ||
        (d.industry || '').toLowerCase().includes(filter.toLowerCase())
      )
    : deals;

  const handleStageChange = (dealId, newStage) => {
    setDeals(deals.map(d => d.id === dealId ? { ...d, pipeline_stage: newStage } : d));
    sales.pipelineSummary().then(r => setSummary(r.data)).catch(() => {});
  };

  const handleDelete = (dealId) => {
    setDeals(deals.filter(d => d.id !== dealId));
    sales.pipelineSummary().then(r => setSummary(r.data)).catch(() => {});
  };

  const handleCreate = (newDeal) => {
    setDeals([newDeal, ...deals]);
    sales.pipelineSummary().then(r => setSummary(r.data)).catch(() => {});
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const totalOpen = summary?.total_open_deals || 0;
  const totalValue = summary?.total_open_value || 0;
  const totalExpected = summary?.total_expected_revenue || 0;
  const contactsBlocked = summary?.contacts_blocked || 0;
  const overdueTasks = summary?.overdue_tasks || 0;
  const sends7d = summary?.sends_last_7d || 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          {totalOpen} open · {formatCurrency(totalExpected)} weighted
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text" placeholder="Filter by company, contact, email…"
            value={filter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '6px 10px', background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13, width: 240 }}
          />
          <button className="btn btn-primary btn-sm" onClick={loadData}>↻ Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewDeal(true)}>➕ New Deal</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 20 }}>
        <Card label="Open Deals" value={totalOpen} color="#3b82f6" />
        <Card label="Pipeline $ Value" value={formatCurrency(totalValue)} color="#8b5cf6" />
        <Card label="Weighted Forecast" value={formatCurrency(totalExpected)} color="#10b981" />
        <Card label="Replies" value={replies.length} color="#f59e0b" />
        <Card label="Sends (7d)" value={sends7d} color="#06b6d4" />
        <Card label="🚫 Blocked" value={contactsBlocked} color={contactsBlocked > 0 ? '#ef4444' : '#374151'} sub={overdueTasks > 0 ? `+${overdueTasks} overdue tasks` : null} />
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1, overflowX: 'auto' }}>
          <div style={{ display: 'flex', minWidth: 'fit-content' }}>
            {STAGES.map(stage => (
              <StageColumn key={stage.id} stage={stage} deals={filteredDeals} onDealClick={setSelectedDeal} />
            ))}
          </div>
        </div>

        <div style={{ flex: '0 0 320px', background: '#111827', borderRadius: 8, padding: 14, alignSelf: 'flex-start' }}>
          <h3 style={{ fontSize: 14, margin: '0 0 12px 0', color: '#f3f4f6' }}>📨 Recent Replies</h3>
          {replies.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 12, textAlign: 'center', padding: 20 }}>No replies yet</div>
          ) : (
            replies.slice(0, 10).map(r => (
              <div key={r.id} style={{ padding: 10, marginBottom: 8, background: '#0f172a', borderRadius: 4, borderLeft: `3px solid ${sentimentColor(r.sentiment)}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#f3f4f6' }}>{r.from_name || r.from_email}</span>
                  <span style={{ fontSize: 10, color: '#6b7280' }}>{formatDate(r.received_at)}</span>
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{r.subject || '(no subject)'}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {r.intent && <span style={{ fontSize: 10, padding: '1px 6px', background: '#1e293b', borderRadius: 8, color: '#cbd5e1' }}>{intentLabel(r.intent)}</span>}
                  {r.bounced === 1 && <span style={{ fontSize: 10, padding: '1px 6px', background: '#7f1d1d', borderRadius: 8, color: '#fecaca' }}>bounced</span>}
                  {r.direction === 'outbound' && <span style={{ fontSize: 10, padding: '1px 6px', background: '#1e3a8a', borderRadius: 8, color: '#bfdbfe' }}>sent</span>}
                </div>
                {r.body && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, fontStyle: 'italic', maxHeight: 36, overflow: 'hidden' }}>"{r.body.slice(0, 100)}{r.body.length > 100 ? '…' : ''}"</div>}
              </div>
            ))
          )}
        </div>
      </div>

      {selectedDeal && <DealDetailModal deal={selectedDeal} onClose={() => setSelectedDeal(null)} onStageChange={handleStageChange} onDelete={handleDelete} />}
      {showNewDeal && <NewDealModal onClose={() => setShowNewDeal(false)} onCreate={handleCreate} />}
    </div>
  );
}

function Card({ label, value, color, sub }) {
  return (
    <div style={{ background: '#1f2937', padding: 12, borderRadius: 6, borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#f3f4f6' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// === Replies view ===
// Lists every sales_replies row, with bounce/auto-reply markers. This is what
// the inbox poller populates every 15 min. The operator can mark as "read"
// in their M365 client; this view is a search/audit interface for the CRM.
function RepliesView({ onToast }) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showBouncedOnly, setShowBouncedOnly] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await sales.listReplies({ limit: 200 });
      setReplies(r.data || []);
    } catch (e) {
      onToast && onToast(`❌ ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s so new inbound replies from the poller show up
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = replies.filter(r => {
    if (showBouncedOnly && !r.bounced) return false;
    if (!q) return true;
    const lq = q.toLowerCase();
    return (r.from_email || '').toLowerCase().includes(lq) ||
           (r.from_name || '').toLowerCase().includes(lq) ||
           (r.subject || '').toLowerCase().includes(lq) ||
           (r.body || '').toLowerCase().includes(lq);
  });

  // Sentiment color (from inbox poller)
  const sentColor = (s) => ({
    positive:  { bg: '#065f46', fg: '#a7f3d0' },
    interested:{ bg: '#065f46', fg: '#a7f3d0' },
    neutral:   { bg: '#1e3a8a', fg: '#bfdbfe' },
    negative:  { bg: '#7f1d1d', fg: '#fecaca' },
    not_interested: { bg: '#7f1d1d', fg: '#fecaca' },
    out_of_office:  { bg: '#1e293b', fg: '#94a3b8' },
    auto_reply:     { bg: '#1e293b', fg: '#94a3b8' },
  }[s] || { bg: '#374151', fg: '#d1d5db' });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text" placeholder="Search replies…"
          value={q} onChange={e => setQ(e.target.value)}
          style={{ flex: '0 0 260px', padding: '6px 10px', background: '#0f172a', color: '#f3f4f6', border: '1px solid #374151', borderRadius: 4, fontSize: 13 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9ca3af', cursor: 'pointer' }}>
          <input type="checkbox" checked={showBouncedOnly} onChange={e => setShowBouncedOnly(e.target.checked)} />
          Show bounces only
        </label>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>↻ Refresh</button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          {filtered.length} of {replies.length} replies ·{' '}
          <span style={{ color: '#fecaca' }}>{replies.filter(r => r.bounced).length} bounced</span> ·{' '}
          <span style={{ color: '#a7f3d0' }}>{replies.filter(r => !r.bounced && (r.sentiment === 'positive' || r.sentiment === 'interested')).length} positive</span>
        </div>
      </div>

      {loading && replies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading replies…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          {replies.length === 0
            ? 'No replies tracked yet. The inbox poller runs every 15 min and will populate this list.'
            : 'No replies match the current filter.'}
        </div>
      ) : (
        <div style={{ background: '#111827', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#9ca3af', textTransform: 'uppercase', fontSize: 10 }}>
                <th style={th}>When</th>
                <th style={th}>From</th>
                <th style={th}>Subject</th>
                <th style={th}>Deal</th>
                <th style={th}>Sentiment</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const c = sentColor(r.sentiment);
                const isOpen = expanded === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderTop: '1px solid #1f2937', color: '#e5e7eb', cursor: 'pointer' }}
                        onClick={() => setExpanded(isOpen ? null : r.id)}>
                      <td style={td}>{formatDate(r.received_at)}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{r.from_name || r.from_email}</div>
                        {r.from_name && <div style={{ fontSize: 10, color: '#6b7280' }}>{r.from_email}</div>}
                      </td>
                      <td style={td}>
                        <div>{r.subject || '—'}</div>
                        {r.bounced && <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 2 }}>⚠️ BOUNCE</div>}
                      </td>
                      <td style={td}>
                        {r.deal_id ? <span style={{ color: '#60a5fa' }}>#{r.deal_id}</span> : <span style={{ color: '#6b7280' }}>—</span>}
                      </td>
                      <td style={td}>
                        <span style={{ padding: '2px 6px', background: c.bg, color: c.fg, borderRadius: 8, fontSize: 10 }}>
                          {r.sentiment || r.intent || r.bounced ? 'bounce' : 'neutral'}
                        </span>
                        {r.intent && <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>{r.intent}</div>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr style={{ background: '#0a0f1a' }}>
                        <td colSpan={5} style={{ padding: 16, color: '#cbd5e1', fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {(r.body || '(empty)').slice(0, 2000)}{(r.body || '').length > 2000 ? '…' : ''}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
